import { useCallback, useState } from 'react';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useClientTranslation } from '@/lib/core/i18n';
import { SheetHeader } from '@/components/shared/shop/ui/SheetHeader';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CreateNewListSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listType: 'Shop' | 'Events';
  onCreate: (name: string) => Promise<void>;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function CreateNewListSheet({ open, onOpenChange, onCreate }: CreateNewListSheetProps) {
  const t = useClientTranslation();
  const [name, setName] = useState('');
  const [isSubmitting, setSubmitting] = useState(false);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
    setName('');
  }, [onOpenChange]);

  const handleCreate = useCallback(async () => {
    const trimmed = name.trim();
    if (!trimmed || isSubmitting) return;
    setSubmitting(true);
    try {
      await onCreate(trimmed);
      setName('');
    } finally {
      setSubmitting(false);
    }
  }, [name, isSubmitting, onCreate]);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="flex flex-col">
        <SheetHeader title={t('App.Shop.Wishlist.CreateNewList')} />

        {/* List name input */}
        <div className="flex flex-col gap-1 px-4 pt-[9px] pb-10">
          <Label htmlFor="new-list-name" className="font-inter">
            {t('App.Shop.Favorites.ListNameLabel')}
          </Label>
          <Input id="new-list-name" value={name} onChange={e => setName(e.target.value)} />
        </div>

        {/* Create / Cancel actions */}
        <div className="border-jncquoi-neutral-400 flex shrink-0 flex-col gap-1 border-t-[0.5px] px-4 pt-6 pb-10.5">
          <Button
            variant="primary-solid"
            size="md"
            onClick={handleCreate}
            disabled={!name.trim() || isSubmitting}
            className="w-full"
          >
            {t('App.Shop.Favorites.CreateListSubmitButton')}
          </Button>
          <Button variant="tertiary-link" size="sm" onClick={handleCancel} className="w-full">
            {t('App.Labels.Cancel')}
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
